import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Hook optimizado para mostrar loading con delay
 * Útil para evitar flashes de loading en requests rápidos
 * Optimizado para mejor rendimiento y menos re-renders
 */
const useLoading = (isLoading: boolean, delay = 500): boolean => {
  const [shouldShowSpinner, setShouldShowSpinner] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExistingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Si no está cargando, ocultar inmediatamente
    if (!isLoading) {
      clearExistingTimeout();
      setShouldShowSpinner(false);
      return;
    }

    // Si ya está mostrando el spinner, no hacer nada
    if (shouldShowSpinner) {
      return;
    }

    // Configurar timeout para mostrar spinner después del delay
    timeoutRef.current = setTimeout(() => {
      setShouldShowSpinner(true);
      timeoutRef.current = null;
    }, delay);

    // Cleanup
    return clearExistingTimeout;
  }, [isLoading, delay, shouldShowSpinner, clearExistingTimeout]);

  // Cleanup al desmontar
  useEffect(() => {
    return clearExistingTimeout;
  }, [clearExistingTimeout]);

  return shouldShowSpinner;
};

export default useLoading;
